/**
 * ThemeProvider
 *
 * Injects all design-token CSS custom properties onto <html> at runtime.
 * This ensures the theme is the authoritative source - not just global.css -
 * so tokens can be updated once in theme.ts and cascade everywhere.
 *
 * Usage:
 *   Wrap your app once (already done in main.tsx via StoreProvider chain):
 *
 *   <ThemeProvider>
 *     <App />
 *   </ThemeProvider>
 */

import { ReactNode, useEffect, createContext, useContext } from 'react';
import { theme, cssVariables, Theme } from '@/styles/theme';

// ── Context ────────────────────────────────────────────────────────────────
const ThemeContext = createContext<Theme>(theme);

/**
 * useTheme - access the full theme object inside any component.
 *
 * @example
 * const { colors, spacing } = useTheme();
 * <div style={{ color: colors.text.primary, padding: spacing[4] }} />
 */
export function useTheme(): Theme {
  return useContext(ThemeContext);
}

// ── Provider ───────────────────────────────────────────────────────────────
interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  useEffect(() => {
    const root = document.documentElement;

    // Inject all CSS custom properties from the token map
    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Tag root with a data attribute for potential CSS scoping
    root.setAttribute('data-theme', 'finn-dark');

    return () => {
      // Cleanup on unmount (useful in tests / SSR)
      Object.keys(cssVariables).forEach((property) => {
        root.style.removeProperty(property);
      });
      root.removeAttribute('data-theme');
    };
  }, []);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
}
